import { Navigate, Outlet, useLocation } from "react-router";
import { useEffect, useState } from "react";
import { useUserCRUD } from "~/hooks/useUserCRUD";
import { useUserActions, getUserAiModel } from "~/store/userStore";
import { DialogContainer } from "../DialogContainer";
import { PendingReportSockets } from "../PendingReportSockets";
import { useDialogActions } from "~/store/dialogStore";
import { EulaDialog } from "~/containers/report/EulaDialog";
import { Check } from "lucide-react";
import { AUTH_STORE_INSTANCE } from "~/store/auth";
import { useIsAuthorized } from "~/store/auth/selectors";
import { useCredentialStoreEvents } from "~/store/credentials";
import { useIsPasswordExpired } from "~/store/credentials";
import { useSessionHeartbeat } from "~/hooks/useSessionHeartbeat";
import { useInviteLinkAutoOpen } from "~/hooks/useInviteLinkAutoOpen";
import { Loading } from "snap-design-system";

// Responsável por proteger rotas que requerem autenticação
// Redireciona para a página de login se o usuário não estiver autenticado
// Também verifica se o usuário aceitou os termos de uso e exibe o diálogo se necessário
export default function ProtectedRoute() {
  const location = useLocation();
  const { useQueryUser } = useUserCRUD();
  const { data: userData, isLoading, isError, isFetching } = useQueryUser;
  const { setUser, setUserSalt, setHasAcceptedTerms, setUserCredits } = useUserActions();
  const { openDialog } = useDialogActions();
  const [hasShownDialog, setHasShownDialog] = useState(false);
  // Indica que o usuário já foi carregado com sucesso ao menos uma vez
  const [hasLoadedUser, setHasLoadedUser] = useState(false);
  const {
    events: { startNewAuthenticatedSession },
  } = AUTH_STORE_INSTANCE;
  const { setVerifiers } = useCredentialStoreEvents();
  const isAuthorized = useIsAuthorized();
  const isPasswordExpired = useIsPasswordExpired();

  // Verifica se a sessão expirou a cada 5 minutos
  useSessionHeartbeat({ checkWhenHidden: true });

  // O dialog de convite só é elegível depois que a sessão está completamente
  // aberta: usuário autenticado, EULA aceita, senha de criptografia inserida
  // (isAuthorized vira true no SecretKeyDialog) e senha ainda válida. Sem
  // isso, o modal de aceite atropelaria o de senha de criptografia.
  const isUserReady =
    !!userData && !!userData.accept_terms && isAuthorized && !isPasswordExpired;
  const { InviteLinkDialog } = useInviteLinkAutoOpen(isUserReady);

  useEffect(() => {
    if (userData) {
      setHasLoadedUser(true);
      startNewAuthenticatedSession({
        user: {
          id: userData.user_id,
          email: userData.email,
          name: userData.name,
        },
      });
      setVerifiers({
        iv: userData.verifier?.iv || null,
        salt: userData.salt || null,
        encrypted: userData.verifier?.encrypted || null,
      });
      // Preserva ai_model do store se já existir (ex: após refresh)
      const currentAiModel = getUserAiModel();
      setUser({ ...userData, ai_model: currentAiModel });
      setUserCredits(userData?.credits_minimun || 0);
      setUserSalt(userData?.salt || null);
      setHasAcceptedTerms(!!userData?.accept_terms);
    }
  }, [userData, setUser, setUserSalt, setHasAcceptedTerms]);

  useEffect(() => {
    if (userData && !userData.accept_terms && !hasShownDialog) {
      console.log(userData);
      setHasShownDialog(true);
      openDialog({
        title: "Termos de Uso",
        icon: <Check />,
        content: <EulaDialog.Content />,
        footer: <EulaDialog.Footer />,
        className: "max-w-4xl",
        preventOutsideClose: true,
        hideCloseButton: true,
      });
    }
  }, [userData, hasShownDialog, openDialog]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <Loading size="lg" />
      </div>
    );
  }

  // Evita redirecionar para login durante refetch após aceitação dos termos
  if ((isError || !userData) && isFetching) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <DialogContainer />
        <Loading size="lg" />
      </div>
    );
  }

  // Só redireciona para login se nunca carregou o usuário com sucesso
  if ((isError || !userData) && !hasLoadedUser) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Usuário já foi carregado antes mas o refetch falhou — mantém a tela com loading
  if (isError || !userData) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <DialogContainer />
        <Loading size="lg" />
      </div>
    );
  }

  if (!userData.accept_terms) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <DialogContainer />
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <>
      <PendingReportSockets />
      <DialogContainer />
      <InviteLinkDialog />
      <Outlet />
    </>
  );
}
